import type { CalendarEntry, HeadlineAct, LondonDay } from '~/api/types'

/**
 * The tabs, and what each one claims.
 *
 * Ported from `_calendar_page.py`'s `VIEWS`. The wording matters: these are the sentences
 * under the tab row, and each one says what the list is *and* who can see it, because the
 * personal ones are about a person and a reader has no other way to find out.
 *
 * The order is the row's order. My venues sits after Curated and before Everything because
 * on a phone the row scrolls and the visible strip is its head: a tab placed earlier would
 * push Curated, the default, off-screen for every signed-in phone reader.
 */
export const VIEWS = [
  {
    key: 'liked',
    label: 'Saved',
  },
  {
    key: 'going',
    label: 'Going',
  },
  {
    key: 'friends',
    label: 'Friends going',
  },
  {
    key: 'picked',
    label: 'Curated',
  },
  {
    key: 'venues',
    label: 'My venues',
  },
  {
    key: 'all',
    label: 'Everything',
  },
] as const

export type ViewKey = (typeof VIEWS)[number]['key']
export type Grid = 'month' | 'week' | 'list'
export type Density = 'full' | 'compact'

/** The ones that are about the reader rather than about London. Hidden when nobody is. */
export const MINE = new Set<ViewKey>(['liked', 'going', 'friends', 'venues'])

export const GRIDS = new Set<Grid>(['month', 'week', 'list'])

/** A signed-out reader has no lists of their own, so the opinionated one is the default. */
export function defaultView(_me: { isEditor: boolean } | null): ViewKey {
  /*
   * The curation, for everybody.
   *
   * A member opened on Saved, which for most of them is a handful of gigs and for a new one
   * is none at all — so the calendar's first screen was six rows or an empty page, on a site
   * whose whole offer is several hundred nights somebody picked by hand. Saved is a lens you
   * choose once you have something in it; it is not what the calendar is.
   */
  return 'picked'
}

/**
 * Whether a tab is this reader's to open.
 *
 * The one rule behind the tab row, the address and the calendar's landing: a tab about
 * the reader needs a reader, and a shared link to one lands a stranger on Curated instead.
 *
 * **My venues is anybody's who has a room.** `tracked` used to be one global bit — the
 * registry plus whatever the editor had starred — so a tab called *My venues* drawn for a
 * reader named the editor's rooms as theirs, and the clause here kept it to the editor. It
 * is now the rooms the reader themself pressed Follow on, one row per person per room, so the
 * tab opens for any signed-in account like the rest of `MINE`. Its twin went with it:
 * the `whole_tracked` flag `calendar_server` passes `_payload`, so the cap sends the asking
 * reader's tracked rows whole.
 *
 * `counts` is optional because the address has none to give. Given, a My venues count of
 * zero hides the tab: the other personal tabs are worth drawing empty (a reader fills them),
 * and this one is not — it is filled from a venue page rather than from anything on this
 * screen, so an empty one would be a tab with nothing to do in it.
 */
export function opens(
  view: ViewKey,
  me: { isEditor: boolean } | null,
  counts?: Partial<Record<ViewKey, number>>,
): boolean {
  if (!me) return !MINE.has(view)
  if (view !== 'venues') return true
  return counts === undefined || (counts.venues ?? 0) > 0
}

/**
 * The tabs this reader is offered, in the row's order.
 *
 * `me` stays `unknown`, as it was: callers hand over whatever `Me` they hold, and all this
 * reads off it is whether there is one and whether it is the editor's.
 */
export function openViews(
  me: unknown,
  counts?: Partial<Record<ViewKey, number>>,
): readonly (typeof VIEWS)[number][] {
  const who = me ? { isEditor: (me as { isEditor?: boolean }).isEditor === true } : null
  return VIEWS.filter(v => opens(v.key, who, counts))
}

/**
 * Which gigs a tab is about.
 *
 * The personal views are a filter over the same list rather than a different fetch — the
 * calendar is one payload, and `liked`, `going`, `friends` and `venues` are questions about
 * rows it already has.
 *
 * Here rather than in the route, because the counts under these tabs are the server's now —
 * `calendar.overview` applies the same predicates — and the browser filtering by one
 * rule while the tab counts by another is a tab that disagrees with the list beneath it.
 * Two implementations is the price of not shipping every row; a rule that can be read side
 * by side with `VIEWS` and tested on its own is the cheapest guard against them drifting.
 *
 * **Curated is `picked`, not `picked || liked`.** The Python has two rules and they are one
 * character apart: `inView` decides what is in a tab and reads `e.picked`, and `onList`
 * decides whether to draw a filled heart and reads `e.picked || e.liked`. This took the
 * second for the first, which put every gig you had hearted into the editorial list — 14 of
 * them, so the tab read 367 where the list it names holds 353. Its own blurb rules it out:
 * Curated is the one list here that is not about what you have done to a gig.
 */
export function inView(
  entry: CalendarEntry,
  view: ViewKey,
  me: { isEditor: boolean } | null = null,
): boolean {
  // The same rule the heart draws itself by. It was `entry.liked` alone, so for the editor a
  // gig could carry a filled heart and be missing from the list of things they had hearted:
  // twenty-three of them, every one either added by hand or headlined by an act they follow.
  // A tab that disagrees with the control on the row is the tab being wrong, because the
  // control is the thing being pressed.
  if (view === 'liked') return onList(entry, me)
  if (view === 'going') return entry.going
  // Going, not saved. `friends` carries both marks because the hover and the sheet name
  // them apart, so a length test files a gig under "Friends going" on the strength of a
  // save alone; the tab is named for the one fact and reads only that flag.
  if (view === 'friends') return (entry.friends ?? []).some(f => f.going)
  if (view === 'picked') return entry.picked
  // The room's mark and nothing else: not `tracked && !picked`, because the tabs are not
  // disjoint and a curated gig at a tracked room belongs on both. The server's count in
  // `calendar.overview` reads the same bit, and the payload's cap sends every tracked row
  // whole so this filter is the night, not a sample of it.
  if (view === 'venues') return entry.tracked
  return true
}


/**
 * Whether a row draws a filled heart.
 *
 * The Python's `onList`, with the half it never had to think about. It read
 * `e.picked || e.liked` because a gig reaches *the editor's* list two ways that are not a
 * personal save — an editor added it, or an act they follow is headlining — and on the
 * Curated tab, where everything is picked by definition, a heart that only meant "saved"
 * left the whole list looking unhearted.
 *
 * That reasoning is entirely about the person whose calendar this is. `picked` is global:
 * it is one editor's opinion, and it goes out to everybody. So for every other reader the
 * same rule filled a heart on gigs they had never touched, and a filled heart on somebody
 * else's page means "I saved this" — which is how the editor's picks came to look like
 * everyone's likes.
 *
 * A heart is yours. For the editor it also covers the list they keep, because for them
 * those are the same act.
 */
export function onList(
  entry: Pick<CalendarEntry, 'picked' | 'liked'>,
  me: { isEditor: boolean } | null,
): boolean {
  return Boolean(me?.isEditor ? entry.picked || entry.liked : entry.liked)
}


/**
 * Whether a star is what is holding this gig on the list, and which act it is.
 *
 * The question `onList` does not answer. A gig can be on the list three ways — the editor
 * added it, somebody hearted it, or a followed act headlines it — and only the third makes
 * the heart the wrong control: pressing it posts a save that cannot take the gig off, so
 * the mark draws itself as done and does nothing. Unfollowing is what removes it.
 *
 * Returns the act rather than a boolean because every caller needs its name to say so, and
 * its id to act on it. It lives here rather than in a view because three views ask it —
 * the compact row, the month chip and the briefing — and the last time it lived in one of
 * them the other two drew a heart that did nothing.
 */
export function heldByStar(entry: CalendarEntry): HeadlineAct | null {
  return entry.starred && entry.act ? entry.act : null
}

/**
 * Where a gig sits in a night, as a rank — lowest first: going, liked, curated, the rest.
 *
 * Four tiers, and this is a reversal. It was cut to two — going, then everything else — on
 * the argument that a heart, a follow and an editor's pick are all the same kind of thing
 * and ranking them against each other buries a gig you hold a ticket for under one you once
 * thought about. The owner asked for the four back, and the first half of that argument
 * survives intact: going is still tier zero and nothing outranks it.
 *
 * The three tiers below it are not one thing. Liked is this reader saying yes; curated is
 * the editor having put it on the calendar at all; the rest is a listing nobody has touched.
 * Those are different facts about a night and the order says so.
 *
 * A gig can be more than one of these, so the tiers are read in order and the strongest
 * wins rather than being summed.
 */
export function byStanding(entry: CalendarEntry): number {
  if (entry.going) return 0
  if (entry.liked) return 1
  if (entry.picked) return 2
  return 3
}

/**
 * Earliest first, and anything with no published time LAST — an empty `time` is not
 * midnight, it is a gig nobody printed an hour for, and it cannot be sorted against one
 * somebody did. `!a.time !== !b.time` is the Python's spelling of "exactly one of these is
 * untimed"; a plain string compare would file every one of them at the top of the night.
 */
export function byTime(a: CalendarEntry, b: CalendarEntry): number {
  if (!a.time !== !b.time) return a.time ? -1 : 1
  return a.time === b.time ? a.title.localeCompare(b.title) : a.time.localeCompare(b.time)
}

/**
 * One night's column, in the order the week draws it: what you have decided about it, then
 * the clock.
 *
 * Only the week re-orders a night. The month cell and the list row take the server's own
 * ranking, which already puts going first, then liked, then picked, and then sorts by score
 * — and a column is the one place that is not enough, because a night's worth of gigs read
 * top to bottom is a running order and wants the clock inside each tier.
 *
 * Which is what went wrong: the rank was two tiers and the tiebreak was the clock, so
 * everything that was not `going` was flattened into one heap and re-sorted by time. The
 * server's liked-before-picked-before-untouched ordering arrived correct and was thrown
 * away by the view that redrew it.
 */
export function inNightOrder(entries: readonly CalendarEntry[]): CalendarEntry[] {
  return [...entries].sort((a, b) => byStanding(a) - byStanding(b) || byTime(a, b))
}

/** How many friends are going and how many have saved it. */
export interface Tally {
  going: number
  saved: number
}

/**
 * The two numbers a calendar row says about your friends.
 *
 * A count and not the names, which is what these places used to print. The month cell drew
 * up to three initials and the list row spelled everybody out, and neither is what a row is
 * for at that size: "AB BC CD" is a puzzle, and a long row of names pushes the act's own
 * name off the line. The names are still one press away, where there is room to say them
 * properly — the sheet names every one of them and says which of the two things they did.
 *
 * Split, because they are two facts. "3 friends" over a gig two people saved and one is
 * going to reads as three people who will be there, which nobody said.
 */
export function tally(friends: readonly { going: boolean }[]): Tally {
  let going = 0
  for (const friend of friends) if (friend.going) going += 1
  return { going, saved: friends.length - going }
}

/** How many names a hover is worth reading before it stops being an answer. */
export const NAMES_SHOWN = 6

/**
 * Who they are, for a hover: "Ada and Bo going · Cy saved".
 *
 * Both halves always, whichever mark was pointed at. A reader hovering the going count is
 * not asking a narrower question than one hovering the saved count — they are asking who,
 * and the answer is everybody who has done either.
 *
 * Capped, because a gig forty of your friends have saved gives a tooltip nobody reads to the
 * end of, and the number is already on the page: the names are for recognising one or two,
 * and past that "and 34 more" is the more useful sentence.
 *
 * Empty when nobody has done either, so a caller can leave the attribute off rather than
 * offering an empty tooltip.
 */
export function whoIs(
  friends: readonly { name: string; going: boolean }[],
  { cap = NAMES_SHOWN, past = false }: { cap?: number; past?: boolean } = {},
): string {
  const going = friends.filter(f => f.going).map(f => f.name)
  const saved = friends.filter(f => !f.going).map(f => f.name)
  // Going first and cut last: it is the stronger of the two facts, so a cap should spend
  // its room on the people who will actually be there.
  const shownGoing = going.slice(0, cap)
  const shownSaved = saved.slice(0, Math.max(cap - shownGoing.length, 0))
  const hidden = friends.length - shownGoing.length - shownSaved.length
  const parts = [
    // `went` on a night that has already happened, the word `WhosGoing` settled on. A hover
    // saying two friends are "going" to a gig last Tuesday is not a small slip: it is the
    // page reporting a plan somebody no longer has.
    shownGoing.length ? `${list(shownGoing)} ${past ? 'went' : 'going'}` : '',
    shownSaved.length ? `${list(shownSaved)} saved` : '',
    hidden > 0 ? `and ${hidden} more` : '',
  ].filter(Boolean)
  return parts.join(' · ')
}

/** "Ada", "Ada and Bo", "Ada, Bo and Cy" — the way a person would say it out loud. */
function list(names: readonly string[]): string {
  if (names.length < 2) return names[0] ?? ''
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/**
 * `tally` as a line: "2 going · 1 saved", or "2 went · 1 saved" on a night that has passed.
 *
 * `saved` needs no tense — it is already past, and it stays true of a gig somebody hearted
 * and did not go to. Only the going half moves.
 */
export function tallyLine(
  friends: readonly { going: boolean }[],
  { past = false }: { past?: boolean } = {},
): string {
  const { going, saved } = tally(friends)
  return [going ? `${going} ${past ? 'went' : 'going'}` : '', saved ? `${saved} saved` : '']
    .filter(Boolean)
    .join(' · ')
}


/**
 * The nights from one to another, inclusive, as `YYYY-MM-DD`.
 *
 * `Date.UTC` and not local time: these are London nights the server already cut at 05:00,
 * and stepping them through a local `Date` would move every one of them by an hour twice a
 * year — which is a day, at the end of a month.
 */
export function nightsFrom(first: LondonDay, last: LondonDay): LondonDay[] {
  const at = new Date(`${first}T00:00:00Z`)
  const end = new Date(`${last}T00:00:00Z`)
  const out: LondonDay[] = []
  // A guard, not a limit: a festival is days and a bad pair of dates is unbounded, and a
  // loop that cannot end takes the page with it.
  while (at <= end && out.length < 60) {
    out.push(at.toISOString().slice(0, 10) as LondonDay)
    at.setUTCDate(at.getUTCDate() + 1)
  }
  return out
}


/**
 * A run of nights, as one entry per night, each saying which night it is.
 *
 * A calendar of nights has no way to say that something lasts four of them. Reading was
 * filed under the Thursday it opens and the Saturday showed nothing — which is the day most
 * people are asking about.
 *
 * So the run appears on each of its nights, and the title says `(Day 2/3)`. Without that a
 * reader sees the same festival three times and reasonably concludes there are three of
 * them; with it, the repetition is the information.
 *
 * The id is not changed. Every copy links to the same gig, carries the same marks and takes
 * the same save — it is one event, drawn where it is on, and the day is a label rather than
 * a fact about a different night.
 *
 * Everything else passes straight through, which is every gig: `lastIso` is null unless a
 * run really crosses a night, so a club night ending at 04:00 stays one row.
 */
export function spread(entries: readonly CalendarEntry[]): CalendarEntry[] {
  const out: CalendarEntry[] = []
  for (const entry of entries) {
    if (!entry.lastIso || entry.lastIso <= entry.iso) {
      out.push(entry)
      continue
    }
    const nights = nightsFrom(entry.iso, entry.lastIso)
    nights.forEach((iso, n) => {
      const copy = { ...entry, iso, title: `${entry.title} (Day ${n + 1}/${nights.length})` }
      madeFrom.set(copy, entry)
      out.push(copy)
    })
  }
  return out
}

/** Each per-night copy `spread` made, to the entry it was made from. */
const madeFrom = new WeakMap<CalendarEntry, CalendarEntry>()

/**
 * The entry as it was handed to `spread`, for one that `spread` drew.
 *
 * A per-night copy's title is a label the calendar made, and its `iso` is the night it was
 * drawn under. Whatever hands the row on as the gig itself — the sheet's seed — needs the
 * row as the server sent it: seeded from the copy, the sheet opens titled "(Day 1/3)" and
 * its heading changes under the reader when `/event` answers with the real title.
 *
 * Any entry `spread` did not make comes back as it is. The lookup is by the object, so it
 * finds only the copy `spread` returned: a further `{ ...copy }` is a new object, is not
 * found, and comes back still carrying its label.
 */
export function unspread(entry: CalendarEntry): CalendarEntry {
  return madeFrom.get(entry) ?? entry
}
